import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { VideoSnapshotsPreview } from "components/common/VideoSnapshotsPreview";
import { AREA_MODAL_DISABLED_SCOPES } from "components/manualReview/hotkeyScopes";
import { useHotkeyContext } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/HotkeyContext";
import { MaterialPicker } from "components/scrapEyeLabelling/components/MaterialPicker";
import { AreaMaterialSnapshot } from "components/scrapEyeLabelling/types";
import { ScrapEyeLayoutMap } from "components/scrapEyeLayout/ScrapEyeLayoutMap";
import { useTenantTranslation } from "hooks/formatters";
import { useMaterials } from "hooks/useMaterials";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAreaLabel } from "../hooks/useAreaLabel";
import {
  useAreaMaterialColors,
  useAreaMaterialLabels,
} from "../hooks/useReportedRailcarMaterials";
import { useHotkeyCodeToAreaMapping } from "../hooks/useAreaHotkeys";
import { useDisableHotkeyScopes } from "../hooks/useDisableHotkeyScopes";
import { usePauseVideoWhileOpen } from "../hooks/usePauseVideoWhileOpen";
import { useWaitForKeyRelease } from "../hooks/useWaitForKeyRelease";

const NO_REPORTED_MATERIALS: Record<string, string> = {};

type ModalTitleProps = Readonly<{
  selectedArea: string | null;
  selectedAreaLabel: string;
  isEditMode: boolean;
  snapshot: AreaMaterialSnapshot | undefined;
}>;

const ModalTitle = ({
  selectedArea,
  selectedAreaLabel,
  isEditMode,
  snapshot,
}: ModalTitleProps) => {
  const { t } = useTenantTranslation();

  if (selectedArea) {
    return <>{t("selectMaterialForArea", { area: selectedAreaLabel })}</>;
  }

  if (isEditMode && snapshot != null) {
    const timestampDisplay = snapshot.timestamp.format("LTS");
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <span>{t("editAreaMappingSnapshot")}</span>
        <Typography
          component="span"
          variant="body2"
          color="text.secondary"
          sx={{ fontWeight: "normal" }}
        >
          ({timestampDisplay})
        </Typography>
      </Stack>
    );
  }

  return <>{t("areaMaterialMapping")}</>;
};

type AreaMaterialMappingModalProps = Readonly<{
  open: boolean;
  onClose: () => void;
  currentMapping: Record<string, string>;
  /**
   * The material expected in each area, kept separate from `currentMapping`.
   * It only pre-selects the picker; it is not a material the labeller has set.
   */
  reportedMaterials?: Record<string, string>;
  /** Whether `reportedMaterials` is still loading, so unmapped areas can show a loading state. */
  isLoadingReportedMaterials?: boolean;
  onUpdateMapping: (area: string, material: string | null) => void;
  snapshot?: AreaMaterialSnapshot;
  onDeleteSnapshot?: (snapshot: AreaMaterialSnapshot) => void;
  disableScopes?: readonly string[];
  onResetToDefaults?: () => void;
  location?: string;
}>;

export const AreaMaterialMappingModal = ({
  open,
  onClose,
  currentMapping,
  reportedMaterials,
  isLoadingReportedMaterials,
  onUpdateMapping,
  snapshot,
  onDeleteSnapshot,
  disableScopes = AREA_MODAL_DISABLED_SCOPES,
  onResetToDefaults,
  location,
}: AreaMaterialMappingModalProps) => {
  const { t } = useTenantTranslation();
  const { materials } = useMaterials();
  const { getMaterialHotkey } = useHotkeyContext();
  const hotkeyCodeToArea = useHotkeyCodeToAreaMapping(location);
  const resolveAreaLabel = useAreaLabel(location);

  const typedSnapshot: AreaMaterialSnapshot | undefined = snapshot;
  const isEditMode = typedSnapshot != null;

  const filteredMaterials = useMemo(() => {
    return (
      materials?.filter((material) => material.labelling_name !== null) ?? []
    );
  }, [materials]);

  const [selectedArea, setSelectedArea] = useState<string | null>(null);

  const hotkeyCodeToMaterialIndex = useMemo(() => {
    const result: Record<string, number> = {};
    for (let i = 0; i < filteredMaterials.length; i++) {
      const code = getMaterialHotkey(i);
      result[code] = i;
    }
    return result;
  }, [filteredMaterials.length, getMaterialHotkey]);

  const unknownMaterialHotkeyCode = getMaterialHotkey(-1);

  useDisableHotkeyScopes(disableScopes, open);
  usePauseVideoWhileOpen(open);

  const keyboardActiveRef = useWaitForKeyRelease(open);

  useEffect(() => {
    if (!open) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- bidirectional state reset on dialog close
      setSelectedArea(null);
    }
  }, [open]);

  const handleSelectArea = useCallback((area: string) => {
    setSelectedArea(area);
  }, []);

  const handleSelectMaterial = useCallback(
    (material: string | null) => {
      if (selectedArea) {
        onUpdateMapping(selectedArea, material);
        setSelectedArea(null); // Go back to area selection
      }
    },
    [selectedArea, onUpdateMapping]
  );

  const handleDelete = useCallback(() => {
    if (typedSnapshot != null && onDeleteSnapshot) {
      onDeleteSnapshot(typedSnapshot);
      onClose();
    }
  }, [typedSnapshot, onDeleteSnapshot, onClose]);

  const areaColors = useAreaMaterialColors(
    currentMapping,
    reportedMaterials ?? NO_REPORTED_MATERIALS
  );

  const areaMaterials = useAreaMaterialLabels(
    currentMapping,
    reportedMaterials ?? NO_REPORTED_MATERIALS
  );

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!keyboardActiveRef.current) {
        return;
      }

      // Ignore if user is typing in an input
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      if (isEditMode && (event.key === "Delete" || event.key === "Backspace")) {
        if (selectedArea) {
          event.preventDefault();
          event.stopPropagation();
          handleSelectMaterial(null);
          return;
        }
      }

      if (selectedArea) {
        if (event.code === unknownMaterialHotkeyCode) {
          event.preventDefault();
          event.stopPropagation();
          handleSelectMaterial("unknown");
          return;
        }

        const materialIndex = hotkeyCodeToMaterialIndex[event.code];
        if (materialIndex !== undefined) {
          const material = filteredMaterials[materialIndex];
          if (material) {
            event.preventDefault();
            event.stopPropagation();
            handleSelectMaterial(material.name);
            return;
          }
        }
      } else {
        const area = hotkeyCodeToArea[event.code];
        if (area) {
          event.preventDefault();
          event.stopPropagation();
          handleSelectArea(area);
        }
      }
    };

    // Use capture phase to intercept before other handlers (including react-hotkeys-hook)
    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
    };
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- keyboardActiveRef is a ref (stable identity), not a reactive dependency
  }, [
    open,
    selectedArea,
    hotkeyCodeToArea,
    hotkeyCodeToMaterialIndex,
    filteredMaterials,
    handleSelectArea,
    handleSelectMaterial,
    isEditMode,
    unknownMaterialHotkeyCode,
  ]);

  const handleDialogClose = useCallback(
    (_event: object, reason: "backdropClick" | "escapeKeyDown") => {
      if (selectedArea !== null) {
        if (reason === "escapeKeyDown") {
          setSelectedArea(null);
          return;
        }
      }
      onClose();
    },
    [selectedArea, onClose]
  );

  const description = selectedArea
    ? isEditMode
      ? t("selectMaterialHintOrDelete")
      : t("selectMaterialHint")
    : isEditMode
      ? t("editSnapshotDescription")
      : t("clickAreaToSetMaterial");

  const selectedMaterialIndex = useMemo(() => {
    if (!selectedArea) return null;
    const material =
      currentMapping[selectedArea] ?? reportedMaterials?.[selectedArea];
    if (!material) return null;
    if (material === "unknown") return -1;
    const materialIndex = filteredMaterials.findIndex(
      (m) => m.name === material
    );
    return materialIndex === -1 ? null : materialIndex;
  }, [selectedArea, currentMapping, reportedMaterials, filteredMaterials]);

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="lg">
      <DialogTitle>
        <ModalTitle
          selectedArea={selectedArea}
          selectedAreaLabel={resolveAreaLabel(selectedArea)}
          isEditMode={isEditMode}
          snapshot={typedSnapshot}
        />
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{ p: 3 }}
        onClick={() => {
          if (selectedArea !== null) {
            setSelectedArea(null);
          }
        }}
      >
        <Stack spacing={3}>
          <VideoSnapshotsPreview compact />

          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
          <Box
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <ScrapEyeLayoutMap
              onSelectArea={handleSelectArea}
              areaColors={areaColors}
              areaMaterials={areaMaterials}
              selectedArea={selectedArea}
              location={location}
              isLoadingAreaMaterials={isLoadingReportedMaterials}
            />
          </Box>

          {selectedArea !== null && (
            <MaterialPicker
              materials={filteredMaterials}
              isEditMode={isEditMode}
              onClearMaterial={() => {
                handleSelectMaterial(null);
              }}
              onCancel={() => {
                setSelectedArea(null);
              }}
              selectedMaterialIndex={selectedMaterialIndex}
            />
          )}
        </Stack>
      </DialogContent>

      {isEditMode && selectedArea === null ? (
        <DialogActions sx={{ justifyContent: "center" }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
          >
            {t("deleteSnapshot")}
          </Button>
        </DialogActions>
      ) : null}

      {!isEditMode && selectedArea === null && onResetToDefaults ? (
        <DialogActions sx={{ justifyContent: "center" }}>
          <Button
            variant="outlined"
            startIcon={<RestartAltIcon />}
            onClick={onResetToDefaults}
          >
            {t("resetToDefault")}
          </Button>
        </DialogActions>
      ) : null}
    </Dialog>
  );
};
